package com.tovtalk.data.repository

import com.russhwolf.settings.Settings
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.entity.*
import com.tovtalk.utils.constants.PreferenceKeys
import com.tovtalk.utils.language.getDeviceLanguageCode
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.strings.BundledFallbackTranslations
import com.tovtalk.utils.strings.TranslationsStore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.flowOf

class SettingsRepositoryImpl(
    private val settings: Settings,
    private val apiClient: ApiClient
) : SettingsRepository {

    companion object {
        const val DEFAULT_LANGUAGE = "en"
        val SUPPORTED_LANGUAGES = listOf("en", "he")
    }

    // Observable language state
    private val _languageFlow = MutableStateFlow(getStoredLanguage())
    
    /** Language: manual user override takes priority, else device locale, else "en". */
    private fun getStoredLanguage(): String {
        val isManuallySelected = settings.getBoolean(PreferenceKeys.IS_LANGUAGE_SELECTED, false)
        val stored = settings.getStringOrNull(PreferenceKeys.APP_LANGUAGE)
        Logger.d("getStoredLanguage: stored='$stored', isManuallySelected=$isManuallySelected", "LangDetect")

        if (isManuallySelected && !stored.isNullOrBlank()) {
            Logger.d("getStoredLanguage: returning manual selection '$stored'", "LangDetect")
            return stored
        }

        // Auto-detect from device — re-check every cold start
        val device = getDeviceLanguageCode()
        val lang = if (device in SUPPORTED_LANGUAGES) device else DEFAULT_LANGUAGE
        Logger.d("getStoredLanguage: device='$device', resolved='$lang'", "LangDetect")
        settings.putString(PreferenceKeys.APP_LANGUAGE, lang)
        // Do NOT set IS_LANGUAGE_SELECTED — only setAppLanguage() (manual choice) does that
        return lang
    }

    private fun getMockAppSettings(): AppSettings = AppSettings(
        id = 1,
        appName = "TovTalk",
        currency = "USD",
        coinValue = 1.0,
        minRedeemCoins = 100,
        minFollowersForLive = 10,
        maxUploadDaily = 10,
        maxStoryDaily = 20,
        maxCommentDaily = 100,
        maxCommentReplyDaily = 100,
        maxPostPins = 3,
        maxCommentPins = 3,
        maxImagesPerPost = 10,
        maxUserLinks = 5,
        maxFileSizeMb = 100,
        liveMinViewers = 0,
        liveTimeout = 60,
        liveBattle = false,
        liveDummyShow = false,
        isCompress = true,
        isDeepAr = false,
        isWithdrawalOn = false,
        isContentModeration = true,
        gifSupport = true,
        watermarkStatus = true,
        zegoAppId = null,
        zegoAppSign = null,
        sightEngineApiUser = null,
        sightEngineApiSecret = null,
        giphyKey = null,
        deeparAndroidKey = null,
        deeparIosKey = null,
        privacyPolicy = null,
        termsOfUse = null,
        helpMail = null
    )

    override suspend fun getAppSettings(): DomainResult<AppSettings> {
        return DomainResult.Success(getMockAppSettings())
    }

    override fun observeAppSettings(): Flow<AppSettings?> {
        return flowOf(getMockAppSettings())
    }

    override suspend fun refreshSettings(): DomainResult<Unit> = DomainResult.Success(Unit)

    override suspend fun getAvailableLanguages(): DomainResult<List<Language>> {
        val languages = listOf(
            Language(
                id = 1,
                code = "en",
                name = "English",
                nativeName = "English",
                isRtl = false,
                isDefault = true,
                csvFileUrl = null,
                isActive = true
            ),
            Language(
                id = 2,
                code = "he",
                name = "Hebrew",
                nativeName = "עברית",
                isRtl = true,
                isDefault = false,
                csvFileUrl = null,
                isActive = true
            )
        )
        return DomainResult.Success(languages)
    }

    override suspend fun setAppLanguage(languageCode: String): DomainResult<Unit> {
        if (languageCode !in SUPPORTED_LANGUAGES) {
            return DomainResult.Error("Unsupported language: $languageCode")
        }
        settings.putString(PreferenceKeys.APP_LANGUAGE, languageCode)
        settings.putBoolean(PreferenceKeys.IS_LANGUAGE_SELECTED, true)
        _languageFlow.value = languageCode
        return DomainResult.Success(Unit)
    }
    
    override suspend fun getAppLanguage(): String {
        return getStoredLanguage()
    }
    
    override fun isLanguageSelected(): Boolean =
        settings.getBoolean(PreferenceKeys.IS_LANGUAGE_SELECTED, false)

    override fun isKashrutSelected(): Boolean {
        return settings.getBoolean(PreferenceKeys.IS_KASHRUT_SELECTED, false)
    }

    override suspend fun setKashrutSelected() {
        settings.putBoolean(PreferenceKeys.IS_KASHRUT_SELECTED, true)
    }

    override suspend fun setOnboardingKashrutLevel(levelId: Int) {
        settings.putString(PreferenceKeys.ONBOARDING_KASHRUT_LEVEL_ID, levelId.toString())
    }

    override fun getOnboardingKashrutLevel(): Int? {
        val raw = settings.getStringOrNull(PreferenceKeys.ONBOARDING_KASHRUT_LEVEL_ID) ?: return null
        return raw.toIntOrNull()
    }

    override fun setOnboardingConsumptionCategoryIds(ids: List<Int>) {
        settings.putString(PreferenceKeys.ONBOARDING_CONSUMPTION_CATEGORY_IDS, ids.joinToString(","))
    }

    override fun getOnboardingConsumptionCategoryIds(): List<Int> {
        val raw = settings.getStringOrNull(PreferenceKeys.ONBOARDING_CONSUMPTION_CATEGORY_IDS) ?: return emptyList()
        return raw.split(",").mapNotNull { it.trim().toIntOrNull() }.filter { it > 0 }
    }

    override fun observeLanguage(): Flow<String> = _languageFlow

    override fun getCurrentLanguage(): String = _languageFlow.value

    override suspend fun loadTranslations(languageCode: String): DomainResult<Map<String, String>> {
        val lang = if (languageCode in SUPPORTED_LANGUAGES) languageCode else DEFAULT_LANGUAGE
        com.tovtalk.utils.strings.ensureBundledTranslationsLoaded(lang)
        val translations = BundledFallbackTranslations.get(lang)
        TranslationsStore.update(translations)
        return DomainResult.Success(translations)
    }

    override suspend fun setPreference(key: String, value: String) {
        settings.putString(key, value)
    }

    override suspend fun getPreference(key: String): String? {
        return settings.getStringOrNull(key)
    }

    override suspend fun clearPreferences() {
        settings.clear()
    }

    private val _audioAutoplayFlow = MutableStateFlow(getStoredAudioAutoplay())
    private fun getStoredAudioAutoplay(): Boolean =
        settings.getBoolean(PreferenceKeys.VIDEO_AUDIO_AUTOPLAY, true)

    override suspend fun getAudioAutoplay(): Boolean = getStoredAudioAutoplay()

    override suspend fun setAudioAutoplay(value: Boolean) {
        settings.putBoolean(PreferenceKeys.VIDEO_AUDIO_AUTOPLAY, value)
        _audioAutoplayFlow.value = value
    }

    override fun observeAudioAutoplay(): Flow<Boolean> = _audioAutoplayFlow
}
